import { useEffect, useState } from 'react';
import { usePortal } from '@/contexts/PortalContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Send, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['General Question', 'Billing Question', 'Schedule Question', 'Design / Selection Question', 'Service / Warranty Question', 'Other'];

export default function PortalSupport() {
  const { api, user } = usePortal();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [category, setCategory] = useState('General Question');
  const pid = user?.projects?.[0];

  useEffect(() => { if (pid) api(`/projects/${pid}/messages`).then(r => setMessages(r.data)).catch(() => {}); }, [api, pid]);

  const handleSend = async () => {
    if (!input.trim() || !pid) return;
    await api(`/projects/${pid}/messages`, { method: 'POST', data: { content: input, category } });
    setInput('');
    toast.success('Message sent');
    api(`/projects/${pid}/messages`).then(r => setMessages(r.data));
  };

  return (
    <div className="space-y-6" data-testid="portal-support">
      <div>
        <h1 className="text-2xl font-extrabold text-[#1e3a50]">Support & Messages</h1>
        <p className="text-sm font-medium text-[#6b8a9e]">Send a message to the HDH team</p>
      </div>

      {/* Message Thread */}
      <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)]">
        <CardContent className="p-4">
          <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <MessageSquare className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-sm font-medium text-[#6b8a9e]">No messages yet. Send us a question!</p>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex gap-2 ${msg.sender === 'homeowner' ? 'justify-end' : ''}`}>
                {msg.sender !== 'homeowner' && (
                  <div className="w-7 h-7 rounded-full bg-[#4a9ac5]/15 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3.5 h-3.5 text-[#2a6496]" />
                  </div>
                )}
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm font-medium ${
                  msg.sender === 'homeowner' ? 'bg-[#4a9ac5] text-white' : 'bg-[#f5f9fb] text-[#2a4a60]'
                }`}>
                  <p>{msg.content}</p>
                  <p className={`text-[9px] mt-1 ${msg.sender === 'homeowner' ? 'text-white/60' : 'text-[#8faab8]'}`}>{new Date(msg.created_at).toLocaleString()}</p>
                </div>
                {msg.sender === 'homeowner' && (
                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-3.5 h-3.5 text-emerald-700" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2">
              <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Type your message..." className="flex-1 text-sm min-h-[40px]"
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())} />
              <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white self-end" onClick={handleSend} disabled={!input.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
